import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoginPage from './components/LoginPage';
import Navbar from './components/Navbar';
import Sidebar from './components/layout/Sidebar';
import ModernBalifyPage from './components/modern/ModernBalifyPage';
import BalifyTablePage from './components/BalifyTablePage';
import BookingDashboard from './components/booking/BookingDashboard';
import BookerAllyPage from './components/booking/BookerAllyPage';
import AccountsPaymentDashboard from './components/accounts/AccountsPaymentDashboardV2';
import AccountsV3Dashboard from './components/accounts/AccountsV3Dashboard';
import HomePage from './components/HomePage';
import HotelSearchPage from './components/HotelSearchPage';
import CalendarView from './components/CalendarView';
import ConvertedItineraryPage from './components/converted-itinerary/ConvertedItineraryPage';
import InvoiceRevampPage from './components/invoice/InvoiceRevampPage';
import HotelManagementV2 from './components/hotels/HotelManagementV2';
import RoleManagement from './components/accounts/RoleManagement';
import PaymentsReportPage from './components/reports/PaymentsReportPage';
import ProfitLossReportPage from './components/reports/ProfitLossReportPage';
import InvoiceReportPage from './components/reports/InvoiceReportPage';
import PaymentReceivedReportPage from './components/reports/PaymentReceivedReportPage';
import PaymentToVendorsReportPage from './components/reports/PaymentToVendorsReportPage';
import IndiaOpsPage from './components/reports/IndiaOpsDashboard';
import IndiaPnLReport from './components/reports/IndiaPnLReport';
import GstLiabilityReport from './components/reports/GstLiabilityReport';
import MyAssignments from './components/work-assignment/MyAssignments';
import CreateEnquiry from './components/work-assignment/CreateEnquiry';
import WorkPool from './components/work-assignment/WorkPool';
import WorkDashboard from './components/work-assignment/WorkDashboard';
import ConditionalWorkWidget from './components/work-assignment/ConditionalWorkWidget';
import AgentRegistrations from './components/AgentRegistrations';
import FxRatePanel from './components/settings/FxRatePanel';
import TreasuryDashboard from './components/treasury/TreasuryDashboard';
import ApiHealthIndicator from './components/ApiHealthIndicator';
import { ToastProvider } from './components/modern/ToastContainer';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { setTokenGetter } from './services/apiWithAuth';
import api from './services/api';

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { error, userRole } = useAuthContext();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  
  // Redirect root to home
  React.useEffect(() => {
    if (location.pathname === '/') {
      navigate('/home');
      return;
    }
  }, [location.pathname, navigate]);

  // Check page access via API
  
  React.useEffect(() => {
    if (location.pathname === '/') return; // Skip check for root path
    const checkPageAccess = async () => {
      try {
        const pagePath = location.pathname === '/' ? '/' : location.pathname;
        const response = await api.get(`/role-pages/access${pagePath}`);
        
        if (response.data.success && !response.data.data.has_access) {
          navigate('/home');
        }
      } catch (error) {
        console.error('Error checking page access:', error);
      }
    };

    checkPageAccess();
  }, [location.pathname, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">Please contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleMobileSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      <Sidebar
        userRole={userRole || 'ADMIN'}
        currentPath={location.pathname}
        onNavigate={handleNavigate}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={handleMobileSidebarClose}
      />
      <div className="flex-1 flex flex-col lg:ml-0 h-screen">
        <Navbar onMobileSidebarToggle={handleMobileSidebarToggle} />
        <main className="flex-1 overflow-y-auto pb-8">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/balify" element={<ModernBalifyPage />} />
            <Route path="/balify-table" element={<BalifyTablePage />} />
            <Route 
              path="/booking" 
              element={<BookingDashboard />} 
            />
            <Route 
              path="/booker-ally" 
              element={<BookerAllyPage />} 
            />
            {/* <Route 
              path="/booking/:itineraryId/manage" 
              element={<ManageBooking />} 
            /> */}
            <Route 
              path="/accounts-dashboard" 
              element={<AccountsPaymentDashboard />} 
            />
            <Route 
              path="/accounts-v3" 
              element={<AccountsV3Dashboard />} 
            />
            <Route path="/search-hotels" element={<HotelSearchPage />} />
            <Route path="/hotel-management" element={<HotelManagementV2 />} />
            <Route path="/role-management" element={<RoleManagement />} />
            <Route path="/confirmed-trips" element={<ConvertedItineraryPage />} />
            <Route path="/invoice-and-payments" element={<InvoiceRevampPage />} />
            <Route path="/calendar" element={<CalendarView />} />
            <Route path="/reports/payments" element={<PaymentsReportPage />} />
            <Route path="/reports/profit-loss" element={<ProfitLossReportPage />} />
            <Route path="/reports/invoices" element={<InvoiceReportPage />} />
            <Route path="/reports/payments-received" element={<PaymentReceivedReportPage />} />
            <Route path="/reports/payments-to-vendors" element={<PaymentToVendorsReportPage />} />
            <Route path="/india-ops" element={<IndiaOpsPage />} />
            <Route path="/reports/india-pnl" element={<IndiaPnLReport />} />
            <Route path="/reports/gst-liability" element={<GstLiabilityReport />} />
            <Route path="/work-assignment" element={<WorkDashboard />} />
            <Route path="/my-assignments" element={<MyAssignments />} />
            <Route path="/create-enquiry" element={<CreateEnquiry />} />
            <Route path="/work-pool" element={<WorkPool />} />
            <Route path="/agent-registrations" element={<AgentRegistrations />} />
            <Route path="/settings/fx-rates" element={<FxRatePanel />} />
            <Route path="/treasury" element={<TreasuryDashboard />} />
          </Routes>
          <ConditionalWorkWidget />
          <ApiHealthIndicator />
        </main>
      </div>
    </div>
  );
}



function AppContent() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AppLayout />
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    setTokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const handleAuthLogout = () => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    };

    window.addEventListener('auth-logout', handleAuthLogout);
    return () => window.removeEventListener('auth-logout', handleAuthLogout);
  }, [logout]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
